import { getTimeAgo } from "../../app/helpers/helper";
import Back from "../../assets/icons/Back";
import ClockIcon from "../../assets/icons/ClockIcon";
import ReloadIcon from "../../assets/icons/ReloadIcon";
import ResumeIcon from "../../assets/icons/ResumeIcon";
import ToggleIcon from "../../assets/icons/ToggleIcon";
import { CONTENT_TYPE } from "../../app/constants";
import CustomModal from "../common/CustomModal/CustomModal";
import EmptyScreen from "../common/EmptyScreen";
import Loader from "../common/Loader";
import LoaderDark from "../common/LoaderDark/LoaderDark";
import "./Watchlist.css";
import gluedin from "gluedin-shorts-js";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const SvodModule = new gluedin.GluedInSvodModule();

export const Watchlist = () => {
  const [progressList, setProgressList] = useState<any[]>([]);
  const [loadingSeriesId, setLoadingSeriesId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removingSeriesId, setRemovingSeriesId] = useState<string | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(true); // initial page
  const [isLoadingMore, setIsLoadingMore] = useState(false); // next pages
  const [hasMore, setHasMore] = useState(true); // stop when false
  const [page, setPage] = useState(1); // offset = page (1-based)
  const LIMIT = 9;

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const seqRef = useRef(0); // guard against out-of-order responses

  const menuRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  // ---- Fetch one page ----
  const fetchPage = async (pageToFetch: number) => {
    const mySeq = ++seqRef.current;

    // select correct loader
    if (pageToFetch === 1) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const response = await SvodModule.getWatchHistory({
        offset: pageToFetch,
        limit: LIMIT,
        incomplete: false, // SDK adds cache buster
      });

      const result = response?.data?.result || [];
      // Ignore stale responses (if any)
      if (mySeq !== seqRef.current) return;

      setProgressList((prev) => {
        // de-dup on seriesId
        const existing = new Set(prev.map((x: any) => x.seriesId));
        const filtered = result.filter((x: any) => !existing.has(x.seriesId));
        return [...prev, ...filtered];
      });

      // If we got fewer than LIMIT, there's no more
      setHasMore(result.length === LIMIT);

      // Advance page only if we appended something (or if server claims more pages)
      if (result.length > 0) {
        setPage(pageToFetch + 1);
      }
    } catch (error) {
      console.error("Failed to fetch watch history:", error);
    } finally {
      if (pageToFetch === 1) setIsLoading(false);
      else setIsLoadingMore(false);
    }
  };

  // ---- Initial load + refresh on BFCache restore ----
  useEffect(() => {
    const resetAndLoad = () => {
      seqRef.current++; // invalidate any in-flight
      setProgressList([]);
      setPage(1);
      setHasMore(true);
      setIsLoading(true); // <-- was false
      setIsLoadingMore(false);
      fetchPage(1);
    };

    resetAndLoad();

    const onPageShow = () => resetAndLoad();
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // ---- Infinite scroll via IntersectionObserver ----
  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          fetchPage(page);
        }
      },
      { root: null, rootMargin: "400px 0px 400px 0px", threshold: 0 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [page, hasMore, isLoading, isLoadingMore]);

  // Handle resume button click
  const handleResume = async (seriesId: string) => {
    try {
      setLoadingSeriesId(seriesId);
      const response = await SvodModule.getSeriesDefinitionById({ seriesId });
      const lastWatched = response?.data?.lastWatchedEpisode;
      if (lastWatched) {
        const safeIndex = Math.max(
          lastWatched.episode === 0 ? 1 : lastWatched.episode - 3,
          1
        );
        window.location.href = `/series/${seriesId}?onScreenElementId=${lastWatched.lastWatchedEpisodeId}&offset=${safeIndex}&contentType=${CONTENT_TYPE.SERIES}`;
      }
    } catch (error) {
      console.error("Failed to fetch series definition:", error);
    } finally {
      setLoadingSeriesId(null);
    }
  };

  // Handle remove button click
  const handleRemove = async (seriesId: string) => {
    try {
      setRemovingSeriesId(seriesId);
      await SvodModule.removeWatchHistory({ seriesId });
      setProgressList((prev) =>
        prev.filter((item) => item.seriesId !== seriesId)
      );
      setConfirmRemoveId(null);
    } catch (error) {
      console.error("Failed to remove watch history:", error);
    } finally {
      setRemovingSeriesId(null);
    }
  };

  // Close 3-dot menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="continue-watching-container">
      <div className="header">
        <button className="back-btn" onClick={() => window.history.back()}>
          <Back />
          {t("back-btn")}
        </button>
        <h2>{t("text-continue-watching")}</h2>
        <select className="filter-dropdown">
          <option>{t("label-series")}</option>
        </select>
      </div>

      {/* Full page loader for first page */}
      {isLoading && <Loader />}

      {!isLoading && !isLoadingMore && progressList?.length === 0 && (
        <EmptyScreen
          className="continue-watching-empty"
          fullHeight={true}
          title={t("label-data-not-found")}
          action={
            <button
              className="reload-btn"
              onClick={() => {
                seqRef.current++;
                setProgressList([]);
                setPage(1);
                setHasMore(true);
                setIsLoading(true);
                setIsLoadingMore(false);
                fetchPage(1);
              }}
            >
              <ReloadIcon />
            </button>
          }
        />
      )}

      {!isLoading && progressList?.length > 0 && (
        <>
          <div className="cards">
            {progressList?.map((item) => {
              const progressPercentage =
                ((item?.episode - 1) / item?.totalEpisode) * 100;

              return (
                <div className="card" key={item?.seriesId}>
                  <div className="thumbnail-wrapper">
                    <img src={item?.thumbnailUrl} alt={item?.title} />
                    <button
                      className="resume-btn"
                      onClick={() => handleResume(item?.seriesId)}
                      disabled={loadingSeriesId === item?.seriesId}
                    >
                      <ResumeIcon />
                      {t("label-resume")}
                    </button>
                  </div>
                  <div className="card-content">
                    <div className="card-header">
                      <h3>{item?.title}</h3>
                      <div className="menu-wrapper">
                        <button
                          className="menu-btn"
                          onClick={() =>
                            setMenuOpenId(
                              menuOpenId === item?.seriesId
                                ? null
                                : item?.seriesId
                            )
                          }
                        >
                          <ToggleIcon />
                        </button>
                        {menuOpenId === item?.seriesId && (
                          <div className="menu-dropdown" ref={menuRef}>
                            <button
                              onClick={() => {
                                setConfirmRemoveId(item.seriesId);
                                setMenuOpenId(null);
                              }}
                            >
                              {t("label-remove-watchlist")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="description">{item?.description}</p>

                    <div className="meta">
                      <span className="time-ago">
                        <ClockIcon />
                        {t("label-watched")}{" "}
                        {getTimeAgo(item?.updatedTimestamp, t)}
                      </span>
                      <div className="meta-episode">
                        <span>Ep. {item?.episode}</span> / {item?.totalEpisode}
                      </div>
                    </div>

                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100, progressPercentage)
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="progress-text">
                        {Math.round(
                          Math.max(0, Math.min(100, progressPercentage))
                        )}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom sentinel + inline loader for next pages */}
          <div ref={loadMoreRef} style={{ height: 1 }} />
          {isLoadingMore && hasMore && (
            <div className="watchlist-loading-more">
              <LoaderDark />
            </div>
          )}
          {!hasMore && (
            <div className="watchlist-end-of-list">
              {/* Optional: end marker */}
            </div>
          )}
        </>
      )}

      {/* Confirmation Popup */}
      {confirmRemoveId && (
        <CustomModal
          isOpen={confirmRemoveId !== null}
          close={() => setConfirmRemoveId(null)}
          classes={"watchlist-confirm-modal"}
        >
          <h4>{t("label-remove-from-watchlist")}</h4>

          <div className="modal-actions">
            <button
              onClick={() => setConfirmRemoveId(null)}
              className="btn_primary btn_cancel m-0"
            >
              {t("btn-cancel")}
            </button>
            <button
              onClick={() => handleRemove(confirmRemoveId)}
              disabled={removingSeriesId === confirmRemoveId}
              className="btn_primary m-0"
            >
              {removingSeriesId === confirmRemoveId
                ? `${t("label-removing")}`
                : `${t("label-remove")}`}
            </button>
          </div>
        </CustomModal>
      )}
    </div>
  );
};
